import _extends from "@babel/runtime/helpers/extends";

/** @jsx jsx */
import { forwardRef, useCallback } from 'react';
import { css, jsx } from '@emotion/core';
import Avatar, { ACTIVE_SCALE_FACTOR, BORDER_WIDTH } from '@atlaskit/avatar';
import { B300, B400, B50, N0, N20, N30, N30A, N500 } from '@atlaskit/theme/colors';
const FONT_SIZE = {
  xsmall: '10px',
  small: '10px',
  medium: '11px',
  large: '12px',
  xlarge: '16px',
  xxlarge: '16px'
};
const buttonActiveStyles = css({
  // eslint-disable-next-line @repo/internal/styles/no-nested-styles
  '&&': {
    backgroundColor: `var(--ds-background-selected, ${B50})`,
    boxShadow: `0 0 0 ${BORDER_WIDTH}px ${`var(--ds-border-selected, ${B300})`}`,
    color: `var(--ds-text-selected, ${B400})`,
    transform: `scale(${ACTIVE_SCALE_FACTOR})`
  }
});
const buttonStyles = css({
  // eslint-disable-next-line @repo/internal/styles/no-nested-styles
  '&&': {
    backgroundColor: `var(--ds-background-neutral, ${N20})`,
    color: `var(--ds-text, ${N500})`,
    fontFamily: 'inherit',
    fontWeight: 500,
    '&:hover': {
      backgroundColor: `var(--ds-background-neutral-hovered, ${N30})`,
      color: `var(--ds-text, ${N500})`,
      '&:after': {
        backgroundColor: `var(--ds-background-neutral-hovered, ${N30A})`,
        opacity: 1
      }
    },
    '&:active': {
      backgroundColor: `var(--ds-background-neutral-pressed, ${B50})`,
      color: `var(--ds-text, ${B400})`,
      '&:after': {
        backgroundColor: 'transparent'
      }
    }
  }
});
const MAX_DISPLAY_COUNT = 99;
const MoreIndicator = /*#__PURE__*/forwardRef(({
  appearance = 'circle',
  borderColor = `var(--ds-border-inverse, ${N0})`,
  size = 'medium',
  count = 0,
  testId,
  onClick,
  'aria-controls': ariaControls,
  'aria-expanded': ariaExpanded,
  'aria-haspopup': ariaHaspopup,
  buttonProps = {},
  isActive
}, ref) => {
  const onClickHander = useCallback((event, analyticsEvent) => {
    if (buttonProps.onClick) {
      buttonProps.onClick(event);
    }

    onClick(event, analyticsEvent);
  }, // eslint-disable-next-line react-hooks/exhaustive-deps
  [buttonProps.onClick, onClick]);
  return jsx(Avatar, {
    appearance: appearance,
    size: size,
    borderColor: borderColor,
    ref: ref,
    onClick: onClickHander
  }, ({
    testId: _,
    className,
    ref,
    ...props
  }) => jsx("button", _extends({
    type: "submit"
  }, buttonProps, props, {
    ref: ref,
    "data-testid": testId,
    "aria-controls": ariaControls,
    "aria-expanded": ariaExpanded,
    "aria-haspopup": ariaHaspopup,
    style: {
      fontSize: FONT_SIZE[size]
    },
    css: [buttonStyles, isActive && buttonActiveStyles],
    className: className
  }), "+", count > MAX_DISPLAY_COUNT ? MAX_DISPLAY_COUNT : count));
});
MoreIndicator.displayName = 'MoreIndicator'; // eslint-disable-next-line @repo/internal/react/require-jsdoc

export default MoreIndicator;